"use client";

import { getSiteContent } from "@/content/site";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import Image from "next/image";
import Link from "next/link";

export function Footer() {
  const { beVersion, feVersion } = useAppContext();
  const { language } = useLanguage();
  const { footerNavLinks, infoRows, legalLinks } = getSiteContent(language);
  const currentYear = new Date().getFullYear();
  const footerCopy = {
    cs: {
      description:
        "Moderní nástroje pro moderní podnikání. Jednoduché, rychlé, spolehlivé.",
      navigation: "Navigace",
      legal: "Právní",
      contact: "Kontakt & Info",
    },
    en: {
      description: "Modern tools for modern business. Simple, fast, reliable.",
      navigation: "Navigation",
      legal: "Legal",
      contact: "Contact & Info",
    },
  }[language];

  return (
    <footer className="rounded-t-3xl border-t border-softiq-primary-shade-600 bg-softiq-primary text-softiq-copy-footer shadow-[0_-18px_60px_rgba(9,25,25,0.22)]">
      <div className="container mx-auto px-6 py-10 sm:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[1.1fr_1fr_1fr]">
          {/* Brand column */}
          <div className="flex flex-col gap-4">
            <span className="text-2xl font-bold uppercase tracking-widest text-white">
              Softiq
            </span>
            <p className="max-w-xs text-sm leading-7 text-softiq-copy-footer-muted">
              {footerCopy.description}
            </p>
            <div className="mt-auto w-fit rounded-xl border border-white/15 bg-white p-2 shadow-sm">
              <Image
                height={1000}
                width={1000}
                alt="GoPay"
                priority
                src="/image/logos/Gopay.svg"
                className="w-auto h-20"
              />
            </div>
          </div>

          {/* Navigation column */}
          <div className="flex flex-col gap-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-softiq-secondary-shade-200">
              {footerCopy.navigation}
            </p>
            {footerNavLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                prefetch={item.href === "/blog" ? false : undefined}
                className="w-fit text-sm text-softiq-copy-footer-muted transition-colors hover:text-white"
              >
                {item.label}
              </Link>
            ))}
            <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-widest text-softiq-secondary-shade-200">
              {footerCopy.legal}
            </p>
            {legalLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="w-fit text-sm text-softiq-copy-footer-muted transition-colors hover:text-white"
              >
                {item.title}
              </Link>
            ))}
          </div>

          {/* Info column */}
          <div className="flex flex-col gap-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-softiq-secondary-shade-200">
              {footerCopy.contact}
            </p>
            {infoRows.map(({ label, value }) => (
              <div key={label} className="flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-wide text-softiq-secondary-shade-200">
                  {label}
                </span>
                <span className="text-sm leading-6 text-softiq-copy-footer-muted">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/15 pt-5 sm:flex-row">
          <Link
            href="https://softiq.cz"
            target="_blank"
            className="text-sm font-semibold uppercase tracking-widest text-white transition-colors hover:text-softiq-secondary-shade-200"
          >
            © <span suppressHydrationWarning>{currentYear}</span> Softiq
          </Link>
          <span className="font-mono text-xs text-softiq-copy-footer-subtle">
            FE {feVersion} | BE {beVersion}
          </span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
